import { min, max } from 'd3-array';

import { Point } from './common-models';
import { TraceViewModel } from './trace-viewmodel';
import { Layout } from '../layout/layout';
import { IChannel, ISample } from '../models/ichannel';
import { Scaler } from '../utils/scaler';
import { Geometry } from '../utils/geometry';
import { EcgMeasures } from '../models/ecgmeasures';

export class CaliperViewModel {
    // data members

    pointer1: PointerViewModel;
    pointer2: PointerViewModel;
    window: WindowViewModel;
    title: string = '';

    private _highlight: boolean = false;
    get highlight() {
        return this._highlight;
    }
    set highlight(highlight: boolean) {
        this._highlight = highlight;
        this.window.opacity = highlight ? this.opacity : 0;
    }

    private _layout: Layout;

    // constructor

    constructor(trace: TraceViewModel, layout: Layout, channel: IChannel) {
        this._layout = layout;

        this.pointer1 = new PointerViewModel(layout, trace, channel, 10, 1);
        this.pointer2 = new PointerViewModel(layout, trace, channel, 30, 2);

        this.window = new WindowViewModel(this);
    }

    // properties

    private _opacity = 1;
    get opacity() {
        return this._opacity;
    }
    set opacity(opacity: number) {
        this._opacity = opacity;

        this.pointer1.opacity = opacity;
        this.pointer2.opacity = opacity;
        this.window.opacity = opacity;
    }

    private _measures: EcgMeasures | null = null;
    get measures(): EcgMeasures | null {
        return this._measures;
    }

    // public functions

    update() {
        this.pointer1.update();
        this.pointer2.update();
    }

    updateMeasures(measures: EcgMeasures) {
        this._measures = measures;
    }

    updatePosition(windowPositionX: number, windowWidth: number, pointerPosition1: number, pointerPosition2: number) {
        this.setPositionX(windowPositionX, windowWidth);
        this.setPointerPosition(1, pointerPosition1);
        this.setPointerPosition(2, pointerPosition2);
    }

    private _x: number = 0;
    private _width: number = 0;
    setPositionX(x: number, window?: number) {
        if (window == null) window = this.window.width;
        const leadWidth = this._layout?.context?.leadWidth ?? 0;
        this.setPointerPosition(1, x - window / 2 >= 0 ? x - window / 2 : 0);
        this.setPointerPosition(2, x + window / 2 <= leadWidth ? x + window / 2 : leadWidth);

        this.window.x = Math.min(this.pointer1.positionX, this.pointer2.positionX);
        this.window.width = Math.abs(this.pointer2.positionX - this.pointer1.positionX);

        this._x = x;
        this._width = this.window.width;
    }

    getPositionX() {
        return this._x;
    }

    getWindow() {
        return this._width;
    }

    setPointerPosition(index: number, x: number) {
        if (index == 1) this.pointer1.positionX = x;
        else this.pointer2.positionX = x;

        this.window.x = Math.min(this.pointer1.positionX, this.pointer2.positionX);
        this.window.width = Math.abs(this.pointer2.positionX - this.pointer1.positionX);
    }
}

export class WindowViewModel {
    // data members

    x: number = 0;
    y: number = 0;
    width: number = 0;
    height: number = 0;
    opacity: number = 0;

    private _caliper: CaliperViewModel;
    get caliper() {
        return this._caliper;
    }

    // constructor

    constructor(caliper: CaliperViewModel) {
        this._caliper = caliper;

        this.opacity = 0;
    }

    // public functions

    update() {
        let measure = Math.abs(
            this.caliper.pointer1.currentIntersection.x - this.caliper.pointer2.currentIntersection.x,
        );
        this.caliper.window.x = Math.min(
            this.caliper.pointer1.currentIntersection.x,
            this.caliper.pointer2.currentIntersection.x,
        );
        this.caliper.window.y = 35;
        this.caliper.window.width = measure;
        this.caliper.window.height = 30;
    }
}

export class PointerViewModel {
    // data members

    intersection: IntersectionViewModel;

    opacity: number = 0;
    selected: boolean = false;
    textX: number = 0;
    tooltip: string = '';
    tooltipOpacity: number = 0;

    layout: Layout;
    trace: TraceViewModel;
    channel: IChannel;

    index;

    // constructor

    constructor(layout: Layout, trace: TraceViewModel, channel: IChannel, positionX: number, index: number) {
        this.opacity = 0;
        this.tooltipOpacity = 0;

        this.layout = layout;
        this.trace = trace;
        this.channel = channel;
        this.index = index;

        this.intersection = new IntersectionViewModel(this);
        this.positionX = positionX;
    }

    // properties

    private _positionX = 0;
    get positionX() {
        return this._positionX;
    }
    set positionX(positionX) {
        const leadWidth = this.layout?.context?.leadWidth ?? 0;
        if (positionX < 0) positionX = 0;

        if (positionX > leadWidth) positionX = leadWidth;

        this._positionX = positionX;

        this.intersection.positionX = this.positionX;

        this.tooltip =
            '(x: ' + this.intersection.positionX.toFixed(3) + ', y: ' + this.intersection.positionY.toFixed(3) + ')';
    }

    get currentIntersection() {
        return this.intersection.getIntersection(this.positionX);
    }

    // public functions

    update() {
        this.intersection.update();
        this.intersection.positionX = this.positionX;

        this.tooltip =
            '(x: ' + this.intersection.positionX.toFixed(3) + ', y: ' + this.intersection.positionY.toFixed(3) + ')';
    }
}

export class IntersectionViewModel {
    // data members

    pointer: PointerViewModel;
    position: Point;

    private unscaler: Scaler | null = null;

    // constructor

    constructor(pointer: PointerViewModel) {
        this.pointer = pointer;

        this.update();

        this.position = {
            x: pointer.positionX,
            y: this.getIntersection(this.pointer.positionX).y,
        };
    }

    // properties

    private _positionX: number = 0;
    get positionX() {
        return this._positionX;
    }
    set positionX(positionX: number) {
        this._positionX = positionX;

        const intersect = this.getIntersection(positionX);
        this._positionY = intersect.y;

        this.position = {
            x: intersect.x,
            y: intersect.y,
        };
    }

    private _positionY: number = 0;
    get positionY() {
        return this._positionY;
    }

    // public functions

    getIntersection(x: number): { x: number; y: number } {
        x = this.unscaler?.onX(x) ?? x;

        const rightPoint1 = { x: 0, y: 0 };
        const rightPoint2 = { x: 0, y: 0 };

        const samples: Array<ISample> = this.pointer.trace.windowedSamples;

        for (let i = 1; i < samples.length; i++) {
            if (i >= x) {
                rightPoint1.x = i - 1;
                rightPoint1.y = samples[i - 1].value;
                rightPoint2.x = i;
                rightPoint2.y = samples[i].value;
                break;
            } else {
                // stroke bottom bug => to avoid NaN with pointer to the end of trace

                rightPoint1.x = samples.length - 2;
                rightPoint1.y = samples[samples.length - 2].value;
                rightPoint2.x = samples.length - 1;
                rightPoint2.y = samples[samples.length - 1].value;
            }
        }

        const scaledPoint1 = { x: x, y: 0 };
        const scaledPoint2 = { x: x, y: 100 };

        const intersection = Geometry.intersection(scaledPoint1, scaledPoint2, rightPoint1, rightPoint2);

        intersection.x = this.pointer.trace?.scaler?.onX(intersection.x) ?? intersection.x;
        intersection.y = this.pointer.trace?.scaler?.onY(intersection.y) ?? intersection.y;

        return intersection;
    }

    update() {
        const minSampleValue = min(this.pointer.channel.samples, (data) => data.value) ?? 0;
        const maxSampleValue = max(this.pointer.channel.samples, (data) => data.value) ?? 0;
        const leadWidth = this.pointer.layout?.context?.leadWidth ?? 0;
        const scaleSettingsX = {
            domain: { min: 0, max: leadWidth },
            range: { min: 0, max: this.pointer.trace.windowedSamples.length },
        };
        const scaleSettingsY = { domain: { min: 100, max: 0 }, range: { min: minSampleValue, max: maxSampleValue } };
        this.unscaler = new Scaler(scaleSettingsX, scaleSettingsY);
    }
}
